import { useCallback, useEffect } from 'react';

import { AnimatePresence } from 'framer-motion';

import { x } from '@/style';

import { ConveyancingSituation } from '@graphql/requests';

import {
  AsyncSelect,
  Form,
  Input,
  RadioButtonGroup,
  Select,
  SubmitFormHandler,
  useForm,
} from '@ui-furniture/common/form';
import { AddressTypeahead } from '@ui-furniture/common/form/address/typeahead';
import { createOutcodeRetrieverForAsyncSelect } from '@ui-furniture/common/outcodes/create-outcode-retriever-for-async-select';
import { Card, Row, RowItem, Text, VStack } from '@ui-furniture/elements';
import { useParam } from '@ui-furniture/hooks/use-param';
import { ChildAnimation } from '@ui-furniture/ui/animation/child';
import { LoadingButton } from '@ui-furniture/ui/loading/button';
import {
  RadioButtons,
  useRadioButtonControls,
} from '@ui-furniture/ui/radio-buttons';

import {
  preferenceOptions,
  purchaseOptions,
  sellerOptions,
} from '../common/quote-options';
import { PurchaseSchema, SaleSchema } from '../common/quote-schema';

const getOutcode = createOutcodeRetrieverForAsyncSelect(10);

const optionConfig = {
  buying: {
    situation: ConveyancingSituation.Buying,
    show: {
      buying: true,
      selling: false,
    },
    schema: PurchaseSchema,
  },
  selling: {
    situation: ConveyancingSituation.Selling,
    show: {
      buying: false,
      selling: true,
    },
    schema: SaleSchema,
  },
  moving: {
    situation: ConveyancingSituation.Moving,
    show: {
      buying: true,
      selling: true,
    },
    schema: PurchaseSchema.merge(SaleSchema),
  },
  remortgaging: {
    situation: ConveyancingSituation.Remortgaging,
    show: {
      buying: false,
      selling: true,
    },
    schema: SaleSchema,
    hideSaleMortgage: true,
    saleTitle: 'Remortgaging Details',
  },
};

export const RecommendationForm = ({
  isSubmitting,
  submit,
}: {
  isSubmitting: boolean;
  submit: SubmitFormHandler<any>;
}) => {
  const defaultType =
    useParam('type', 'string', { isOptional: true }) || 'buying';
  const moveTypeControls = useRadioButtonControls({
    defaultValue: defaultType,
    options: [
      { label: 'Buying', value: 'buying' },
      { label: 'Selling', value: 'selling' },
      { label: 'Both', value: 'moving' },
      { label: 'Remortgaging', value: 'remortgaging' },
    ],
  });
  useEffect(() => {
    moveTypeControls.setSelected(defaultType);
  }, [defaultType]);
  const form = useForm(
    (_, context) => {
      const moveType = context?.moveType;
      if (!moveType) throw new Error('Move type needs to be defined');
      const { schema } = optionConfig[moveType];
      return schema;
    },
    {
      defaultValues: {
        purchaseDetails: {
          // price: 282000,
          buyerCount: 1,
          mortgage: true,
        },
        saleDetails: {
          // price: 282000,
          sellerCount: 1,
          mortgage: true,
        },
      },
      context: {
        moveType: moveTypeControls.selected,
      },
    }
  );

  const moveType = moveTypeControls.selected;
  const { show, situation, hideSaleMortgage, saleTitle } =
    optionConfig[moveType];
  const submitWithType = useCallback(
    (formData) => {
      submit({
        ...formData,
        situation,
      });
    },
    [situation, submit]
  );

  return (
    <x.div>
      <Form
        form={form}
        onSubmit={form.handleSubmit(submitWithType)}
        py='md'
        minW='100%'
      >
        <VStack w='100%' gap='xl'>
          <RadioButtons controls={moveTypeControls} />
          <AnimatePresence>
            {show.buying ? (
              <ChildAnimation>
                <VStack>
                  <Text variant='bodyBig'>Purchase Details</Text>
                  <Card px='xl' py='xl'>
                    <VStack gap='lg'>
                      <Row>
                        <RowItem>
                          <Input
                            name='purchaseDetails.price'
                            label='Property Price'
                            format='currency'
                            placeholder='e.g. £282,000'
                            informationTooltip={'An estimate is fine'}
                            allowNegative={false}
                          />
                        </RowItem>
                        <RowItem flex='unset'>
                          <RadioButtonGroup
                            name='purchaseDetails.buyerCount'
                            label='No of Buyers'
                            options={[
                              { label: 'One', value: 1 },
                              { label: 'Two', value: 2 },
                              { label: 'More', value: 3 },
                            ]}
                          />
                        </RowItem>
                        <RowItem flex='unset'>
                          <RadioButtonGroup
                            name='purchaseDetails.mortgage'
                            label='Mortgage'
                            options={[
                              { label: 'No', value: false },
                              { label: 'Yes', value: true },
                            ]}
                          />
                        </RowItem>
                      </Row>
                      <Row>
                        <RowItem>
                          <AsyncSelect
                            name='purchaseDetails.areaOutcode'
                            label='Area'
                            placeholder='e.g. SW1A - Buckingham Palace'
                            noOptionsMessage={({ inputValue }) =>
                              !inputValue
                                ? 'Start typing the postcode you’re looking in'
                                : 'We couldn’t find that area'
                            }
                            loadOptions={getOutcode}
                          />
                        </RowItem>

                        <RowItem>
                          <Select
                            name='purchaseDetails.tenure'
                            label='Tenure'
                            placeholder='e.g. Freehold (House)'
                            options={purchaseOptions.tenure}
                            informationTooltip={`Don't get confused by the word tenure. You'll probably know if it's Shared Ownership, Freehold is typical for a house, and Leaseholds are typical for flats. Don't worry if you get it wrong, the conveyancer can correct it when you instruct them`}
                          />
                        </RowItem>
                      </Row>
                      <Select
                        name='purchaseDetails.additionalDetails'
                        label='Anything Else'
                        placeholder='e.g. First Time Buyer'
                        isOptional
                        isMulti
                        isSearchable
                        hideSelectedOptions={false}
                        closeMenuOnSelect={false}
                        options={purchaseOptions.additionalDetails}
                      />
                    </VStack>
                  </Card>
                </VStack>
              </ChildAnimation>
            ) : null}
          </AnimatePresence>
          {show.selling ? (
            <ChildAnimation>
              <VStack>
                <Text variant='bodyBig'>{saleTitle || 'Sale Details'}</Text>
                <Card px='xl' py='xl'>
                  <VStack gap='lg'>
                    <Row>
                      <RowItem>
                        <Input
                          name='saleDetails.price'
                          placeholder='e.g. £282,000'
                          format='currency'
                          label='Property Price'
                          informationTooltip={'An estimate is fine'}
                          allowNegative={false}
                        />
                      </RowItem>
                      <RowItem flex={'unset'}>
                        <RadioButtonGroup
                          name='saleDetails.sellerCount'
                          label='No of Sellers'
                          options={[
                            { label: 'One', value: 1 },
                            { label: 'Two', value: 2 },
                            { label: 'More', value: 3 },
                          ]}
                        />
                      </RowItem>
                      {hideSaleMortgage ? null : (
                        <RowItem flex={'unset'}>
                          <RadioButtonGroup
                            name='saleDetails.mortgage'
                            label='Has Mortgage'
                            options={sellerOptions.mortgage}
                          />
                        </RowItem>
                      )}
                    </Row>
                    <AddressTypeahead
                      name='saleDetails.address'
                      label='Address'
                    />
                    <Select
                      name='saleDetails.additionalDetails'
                      placeholder='e.g. Help to Buy'
                      isOptional
                      isMulti
                      isSearchable
                      hideSelectedOptions={false}
                      closeMenuOnSelect={false}
                      label='Anything Else'
                      options={sellerOptions.additionalDetails}
                    />
                  </VStack>
                </Card>
              </VStack>
            </ChildAnimation>
          ) : null}
          <VStack>
            <Text variant='bodyBig'>Preferences</Text>
            <Card px='xl' py='xl'>
              <Row>
                <RowItem>
                  <Select
                    name='urgency'
                    label='I need a conveyancer ...'
                    placeholder='e.g. Now'
                    isOptional
                    options={preferenceOptions.urgency}
                    w='100%'
                  />
                </RowItem>
                <RowItem>
                  <Select
                    name='priority'
                    label={`What’s most important to you?`}
                    placeholder='e.g. Service'
                    isOptional
                    options={preferenceOptions.priority}
                    w='100%'
                  />
                </RowItem>
              </Row>
            </Card>
          </VStack>

          <LoadingButton
            type='submit'
            isLoading={isSubmitting}
            alignSelf={{ _: 'center', tablet: 'end' }}
          >
            Submit
          </LoadingButton>
        </VStack>
      </Form>
    </x.div>
  );
};
